import { CSSProperties } from "react";
import { IWebsiteBuilderElementBase, IWebsiteBuilderElementDataBase, TWebsiteBuilderElement } from "./websiteBuilderElement";
import classNames from "classnames";
import { websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";

export type TWebsiteBuilderLogoObjectFit = "contain" | "cover" | "fill";

function isWebsiteBuilderLogoObjectFit(value: string): value is TWebsiteBuilderLogoObjectFit {
  return value === "contain" || value === "cover" || value === "fill";
}

export interface IWebsiteBuilderLogoSettings {
  objectFit: TWebsiteBuilderLogoObjectFit;
  alt: string;
}

export interface IWebsiteBuilderLogoData extends IWebsiteBuilderElementDataBase {
  type: "logo";
  settings: IWebsiteBuilderLogoSettings;
}

export interface IWebsiteBuilderLogo extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderLogoData;
}

export function isWebsiteBuilderLogo(node: TWebsiteBuilderElement): node is IWebsiteBuilderLogo {
  return node?.element?.type === "logo";
}

function generateDefaultLogoSettings(): IWebsiteBuilderLogoSettings {
  return {
    objectFit: "contain",
    alt: "",
  };
}

export function websiteBuilderGenerateDefaultLogo(): IWebsiteBuilderLogo {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "logo",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 75, height: 75 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 75, height: 75 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 75, height: 75 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultLogoSettings(),
    },
  };
}

export function websiteBuilderNormalizeLogoSettings(partialSettings: Partial<IWebsiteBuilderLogoSettings>): IWebsiteBuilderLogoSettings {
  const defaultSettings = generateDefaultLogoSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { objectFit, alt } = partialSettings;

  const normalizedLogoSettings: IWebsiteBuilderLogoSettings = {
    objectFit: websiteBuilderNormalizeValue({
      type: "string",
      value: objectFit,
      defaultValue: defaultSettings.objectFit,
      required: true,
      validate: isWebsiteBuilderLogoObjectFit,
    }) as TWebsiteBuilderLogoObjectFit,
    alt: websiteBuilderNormalizeValue({
      type: "string",
      value: alt,
      defaultValue: defaultSettings.alt,
      required: true,
    }),
  };

  return normalizedLogoSettings;
}

interface IWebsiteBuilderLogoDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
}

export function websiteBuilderGenerateLogoDesignStyles(settings: IWebsiteBuilderLogoSettings, src: string): IWebsiteBuilderLogoDesignStyles {
  const styleProperties: CSSProperties = {
    objectFit: settings.objectFit,
  };

  const styleDeclaration = `object-fit: ${styleProperties.objectFit};`;
  
  return {
    containerClassName: classNames("website-builder-logo-container", {
      "no-src-border": !src,
    }),
    className: "website-builder-logo",
    styleProperties,
    styleDeclaration,
  };
}